import { useContext, useEffect, useMemo, useState } from "react";
import * as ProfileService from "../../Service/ProfileService";
import OtpForm from "../../../../components/OtpForm";
import Spinner from "../../../../components/Spinner";
import { useTranslation } from "react-i18next";
import { toastMsg } from "../../../../utils/swal";
import CustomPhoneInput from "../../../../components/CustomPhoneInput";
import threeDot from "../../../../assets/images/icons/dots.svg";
import { Link } from "react-router-dom";
import addCircle from "../../../../assets/images/icons/add-circle.svg";
import QuestionBox from "../../../../components/QuestionBox";
import { LoginContext } from "../../../../utils/Auth";

const ProfilePhoneNumbersEdit = ({
  profileData,
  closeModal,
  setProfileData,
  verify,
  isProUser,
  startInAddMode = false,
  askFullName = false,
}) => {
  const { t } = useTranslation();
  const { user, setUser } = useContext(LoginContext);
  const [loading, setLoading] = useState(false);
  const [profileInfo, setProfileInfo] = useState({
    extra_phone_number: profileData?.phone_numbers || [],
    phone_number: profileData?.phone_number || [],
  });
  const [phoneNumber, setPhoneNumber] = useState("");
  const [selectedPhone, setSelectedPhone] = useState(null);
  const [addNewPhone, setAddNewPhone] = useState(startInAddMode);
  const [editPhone, setEditPhone] = useState(null);
  const [deletePhone, setDeletePhone] = useState(null);
  const [phoneToVerify, setPhoneToVerify] = useState(null);
  const [fullName, setFullName] = useState(
    profileData?.full_name || user?.profile?.full_name || "",
  );
  const [fullNameTouched, setFullNameTouched] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      let res = null;
      if (editPhone?.id) {
        res = await ProfileService.profileEdit({
          extra_phone_number: [
            {
              id: editPhone?.id,
              phone_number: phoneNumber,
            },
          ],
        });
      } else if (editPhone?.isPrimary) {
        res = await ProfileService.profileEdit({
          phone_number: phoneNumber,
        });
      } else {
        res = await ProfileService.profileEdit({
          extra_phone_number: [
            {
              phone_number: phoneNumber,
            },
          ],
        });
      }

      let updatedFullName = fullName;
      if (askFullName) {
        const body = {
          full_name: fullName,
          username: profileData?.username || user?.username || "",
        };
        const userRes = await ProfileService.userEdit(body);
        if (userRes?.data?.profile?.full_name) {
          updatedFullName = userRes.data.profile.full_name;
        }

        try {
          await ProfileService.sendRegistrationWhatsapp({
            phone_number: phoneNumber,
          });
        } catch (waErr) {
          console.error("Error sending WA registration msg:", waErr);
        }
      }
      setProfileData((prev) => ({
        ...prev,
        phone_number: res.phone_number || prev.phone_number,
        phone_numbers:
          res.phone_numbers || prev.phone_numbers,
        full_name: askFullName ? updatedFullName : prev?.full_name,
      }));
      setProfileInfo((prev) => ({
        ...prev,
        phone_number: res.phone_number || prev.phone_number,
        extra_phone_number: res.phone_numbers || prev.extra_phone_number,
      }));
      setUser((prev) => ({
        ...prev,
        profile: {
          ...prev?.profile,
          phone_number: res.phone_number || prev?.profile?.phone_number,
          phone_numbers:
            res.phone_numbers || prev?.profile?.phone_numbers,
          full_name: askFullName ? updatedFullName : prev?.profile?.full_name,
        },
        full_name: askFullName ? updatedFullName : prev?.full_name,
      }));
      setAddNewPhone(false);
      closeModal();
    } catch (err) {
      toastMsg(t("errors.systemError"), "#ff0000", "#ffffff");
    } finally {
      setLoading(false);
      setSelectedPhone(null);
      setEditPhone(null);
      setPhoneNumber("");
    }
  };

  const removePhone = async () => {
    setLoading(true);
    try {
      const res = await ProfileService.deleteProfileDetail({
        field_name: "extra_phone_number",
        id: deletePhone?.id,
      });
      const filtered = profileData?.phone_numbers?.filter(
        (item) => item.id !== deletePhone?.id,
      );
      setProfileData((prev) => ({
        ...prev,
        phone_numbers: filtered,
      }));
      setProfileInfo((prev) => ({
        ...prev,
        extra_phone_number: filtered,
      }));
      setUser((prev) => ({
        ...prev,
        profile: {
          ...prev.profile,
          phone_numbers: filtered,
        },
      }));
      setDeletePhone(null);
      toastMsg(t("profilePhoneEdit.phoneDeleted"));
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
      closeModal();
    }
  };

  // const primaryPhone = async () => {
  //   setLoading(true);
  //   try {
  //     const res = await ProfileService.profileEdit({
  //       phone_number: selectedPhone?.phone_number,
  //     });
  //     console.log(res);
  //   } catch (error) {
  //     console.log(error);
  //   } finally {
  //     setLoading(false);
  //     setVerifyPhone(false);
  //     setSelectedPhone(null);
  //   }
  // };

  useEffect(() => {
    if (verify) {
      setPhoneToVerify(verify);
    }
  }, [verify]);

  const isButtonDisabled = useMemo(() => {
    let disabled = false;
    const normalized = phoneNumber.replace(/\D/g, "");
    const full = "+".concat(normalized);
    if (full.startsWith("+994")) {
      const regex = /^\+994(50|51|55|70|77|99)\d{7}$/;
      disabled =
        phoneNumber?.length < 13 ||
        !regex.test(full) ||
        /^(\+994(50|51|55|70|77|99))0{7}$/.test(full);
    } else {
      disabled = phoneNumber?.length < 2 || /^0+$/.test(normalized);
    }

    if (askFullName) {
      if (!fullName || fullName.trim().length < 3) {
        disabled = true;
      }
    }

    return disabled;
  }, [phoneNumber, fullName, askFullName]);

  // Check if any phone number is already verified
  const hasVerifiedPhone = profileInfo?.extra_phone_number?.some(
    (item) => item.is_verified,
  );

  if (loading) {
    return <Spinner />;
  }

  return (
    <div className="profilePhoneEdit">
      {addNewPhone ? (
        <form className="phoneForm" onSubmit={onSubmit}>
          {askFullName && (
            <div className="phoneBox" style={{ marginBottom: "16px" }}>
              <label htmlFor="fullName">
                {t("profileInfoEdit.fullName")} *
              </label>
              <input
                id="fullName"
                type="text"
                className="customInput"
                style={{
                  width: "100%",
                  padding: "12px",
                  border: "1px solid #ccc",
                  borderRadius: "8px",
                  outline: "none",
                  fontSize: "16px",
                }}
                value={fullName}
                onChange={(e) => {
                  setFullName(e.target.value);
                  setFullNameTouched(true);
                }}
                onBlur={() => setFullNameTouched(true)}
              />
              {fullNameTouched && (!fullName || fullName.trim().length < 3) && (
                <span
                  className="errorAlert"
                  style={{
                    color: "red",
                    fontSize: "12px",
                    marginTop: "4px",
                    display: "block",
                  }}
                >
                  {t("profileInfoEdit.minChar") || "Minimum 3 characters"}
                </span>
              )}
            </div>
          )}
          <div className="phoneBox">
            <label htmlFor="phone">{t("login.phone")} *</label>
            <CustomPhoneInput
              value={phoneNumber}
              onChange={(val) => setPhoneNumber(val)}
            />
            {/* {true && <span className="errorAlert">"sa"</span>} */}
          </div>
          <button
            className="btn"
            onClick={onSubmit}
            disabled={isButtonDisabled}
          >
            {t("settings.save")}
          </button>
        </form>
      ) : (
        <>
          <ul className="phones" onClick={() => setSelectedPhone(null)}>
            {profileInfo?.extra_phone_number?.map((item) => (
              <li className="phone" key={item.id}>
                <div
                  className={`optionsOverlay ${
                    selectedPhone?.id === item.id ? "active" : ""
                  }`}
                  onClick={() => setSelectedPhone(null)}
                ></div>
                <Link to={`tel:${item.phone_number}`}>{item.phone_number}</Link>
                <div
                  className="icon"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedPhone(item);
                  }}
                >
                  <img src={threeDot} alt="three-dot-icon" />
                  <div
                    className={`options ${
                      selectedPhone?.id === item.id ? "active" : ""
                    }`}
                  >
                    <p
                      className="optionItem"
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditPhone(item);
                        setPhoneNumber(item.phone_number);
                        setAddNewPhone(true);
                        setSelectedPhone(null);
                      }}
                    >
                      {t("profilePhoneEdit.edit")}
                    </p>
                    {!item.is_verified && isProUser && (
                      <p
                        className="optionItem"
                        onClick={(e) => {
                          e.stopPropagation();
                          setPhoneToVerify(item);
                          setSelectedPhone(null);
                        }}
                      >
                        {t("profilePhoneEdit.verify")}
                      </p>
                    )}
                    <p
                      className="optionItem"
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeletePhone(item);
                        setSelectedPhone(null);
                      }}
                    >
                      {t("profilePhoneEdit.delete")}
                    </p>
                  </div>
                </div>
              </li>
            ))}
          </ul>
          {!addNewPhone && profileInfo?.extra_phone_number?.length < 2 && (
            <div className="addBtn">
              <button
                className="btnAdd"
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setAddNewPhone(true);
                }}
              >
                <img src={addCircle} alt="add-icon" />
                {t("profileEditExperience.add")}
              </button>
              <button className="btnCancel" onClick={() => closeModal()}>
                {t("settings.cancel")}
              </button>
            </div>
          )}
        </>
      )}
      {deletePhone && (
        <QuestionBox
          onCancel={() => {
            setDeletePhone(null);
          }}
          onAccept={async () => await removePhone()}
          title={t("profilePhoneEdit.deleteTitle")}
          details={t("profilePhoneEdit.deleteDetails")}
          acceptBtnText={t("profilePhoneEdit.delete")}
        />
      )}
      {phoneToVerify && (
        <OtpForm
          onSubmit={(verified) => {
            if (verified) {
              setPhoneToVerify(null);
              closeModal();
            }
          }}
          phone={phoneToVerify?.phone_number}
          onCancel={() => {
            setPhoneToVerify(null);
            closeModal();
          }}
        />
      )}
    </div>
  );
};

export default ProfilePhoneNumbersEdit;
